import {
  Component,
  ComponentRef,
  OnInit,
  ViewChild,
  ViewContainerRef,
  computed,
  effect,
  inject,
} from '@angular/core';
import {
  AppStore,
  appEvents,
} from '@navstation-portal/reporting/data-access/app';
import { EngineReportStore } from '@navstation-portal/reporting/data-access/engine-report';
import {
  EngineReportsStore,
  engineReportsEvents,
} from '@navstation-portal/reporting/data-access/engine-reports';
import { OffshoreReportsStore } from '@navstation-portal/reporting/data-access/offshore-reports';
import { ReportStore } from '@navstation-portal/reporting/data-access/vessel-report';
import {
  VesselReportsStore,
  vesselReportsEvents,
} from '@navstation-portal/reporting/data-access/vessel-reports';
import { VesselSpecStore } from '@navstation-portal/vessels/data-access/store';
import { Events, injectDispatch } from '@ngrx/signals/events';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { PanelMenuComponent } from './panel-menu/panel-menu.component';
import {
  ALL_CREATE_MENU_LABELS,
  HideOnAnyIncompleteOffshoreReport,
  HideOnAnyIncompleteVesselReport,
  HideOnDieselElectricEngineReport,
  HideOnIncompleteEngineReport,
  EngineReportType,
  REPORT_TYPE_MENU_LABELS,
  ShowCrewDashboardClient,
  ShowEditOffshoreReportInProgress,
  ShowEditReportInProgress,
  ShowEmissionsClient,
  ShowFromNavFleet,
  ShowOnDieselElectricEngineReport,
  ShowOnIncompleteEngineReport,
  ShowOnIncompleteVesselReportOffshore,
  ShowOnIncompleteVesselReportReg,
  ShowOnInternalOnly,
  SideMenuItem,
  VisibilityMapping,
  routes,
} from './side-menu-constants';

import { CollapsedPanelMenuComponent } from './collapsed-panel-menu/collapsed-panel-menu.component';

@Component({
  selector: 're-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrl: './side-menu.component.css',
  imports: [ButtonModule, FontAwesomeModule],
})
export class SideMenuComponent implements OnInit {
  @ViewChild('sideMenu', { read: ViewContainerRef, static: true })
  containerSideMenu!: ViewContainerRef;
  componentRef!: ComponentRef<PanelMenuComponent>;

  @ViewChild('collapsedSideMenu', { read: ViewContainerRef, static: true })
  containerCollapsedSideMenu!: ViewContainerRef;
  collapsedComponentRef!: ComponentRef<CollapsedPanelMenuComponent>;

  // Menu Control Variables
  // (To understand how these variables should control the menu please see the wiki)
  isInternal = false;
  hasEnteredAppFromNavFleet = false;
  isCrewDashboardClient = true;
  //  Vessel Reports Menu Control Variables
  hasIncompleteVesselRegularReport = false;
  hasIncompleteVesselOffshoreReport = false;
  //  Engine Reports Menu Control Variables
  isEmissionComplianceClient = true;
  isDieselElectricPropulsion = false;
  hasIncompleteEngineReport = false;
  typeOfEngineReportInProgress = EngineReportType.MainEngine;
  editEngineReportLabel = 'Edit ME Report in Progress';

  vesselSpecStore = inject(VesselSpecStore);
  vesselReportsStore = inject(VesselReportsStore);
  reportStore = inject(ReportStore);
  engineReportsStore = inject(EngineReportsStore);
  engineReportStore = inject(EngineReportStore);
  offshoreReportsStore = inject(OffshoreReportsStore);
  appStore = inject(AppStore);
  private events = inject(Events);
  private vesselReportsDispatch = injectDispatch(vesselReportsEvents);
  private engineReportsDispatch = injectDispatch(engineReportsEvents);

  showIncompleteEngineReport: VisibilityMapping[] =
    ShowOnIncompleteEngineReport;

  faChevronLeft = faChevronLeft;
  faChevronRight = faChevronRight;
  isCollapsed = false;

  enforceReportSequence = computed(
    () => this.vesselSpecStore.vm()?.formConfig?.enforceReportSequence ?? true,
  );

  routes = computed(() => {
    const vesselSpecVm = this.vesselSpecStore.vm();
    if (!vesselSpecVm) return routes;

    const { isVesselReportingEnabled, isOffshoreReportingEnabled } =
      vesselSpecVm.reportingOptions || {};

    return routes.map((route) => {
      if (route.id === 'vessel-reports') {
        route.visible = !!isVesselReportingEnabled;
      }
      if (route.id === 'offshore-reports') {
        route.visible = !!isOffshoreReportingEnabled;
      }
      return route;
    });
  });

  constructor() {
    this.events
      .on(appEvents.setSelectedVesselId)
      .pipe(takeUntilDestroyed())
      .subscribe(({ payload: newVesselId }) => {
        this.vesselReportsDispatch.reset();
        this.vesselReportsDispatch.fetchVesselReports(newVesselId);
        this.engineReportsDispatch.reset();
        this.engineReportsDispatch.fetchEngineReports(newVesselId);
      });

    effect(() => {
      const updatedRoutes = this.routes();
      if (!this.componentRef) return;

      this.componentRef.instance.routes = updatedRoutes;
    });

    effect(() => {
      const vesselReportsVm = this.vesselReportsStore.vm();
      const localReportId = this.reportStore.localReportId();
      const enforceSequence = this.enforceReportSequence();
      const offshoreReportsVm = this.offshoreReportsStore.vm();
      if (!this.componentRef) return;

      this.updateVesselReportMenuState(
        vesselReportsVm,
        localReportId,
        enforceSequence,
      );
      this.updateOffshoreReportMenuState(offshoreReportsVm.incompleteReportId);
      this.resetMenu();
    });

    effect(() => {
      const vm = this.engineReportsStore.vm();
      const localReportId = this.engineReportStore.localReportId();
      if (!this.componentRef) return;
      this.updateEngineReportMenuState(vm.incompleteReportId, localReportId);
    });
  }

  ngOnInit() {
    const vesselId = this.appStore.selectedVesselId();
    if (vesselId) {
      this.vesselReportsDispatch.fetchVesselReports(vesselId);
      this.engineReportsDispatch.fetchEngineReports(vesselId);
    }
    this.resetMenu();
  }

  toggleMenu(): void {
    this.isCollapsed = !this.isCollapsed;
    this.resetMenu();
  }

  resetMenu() {
    this.containerSideMenu.clear();
    this.containerCollapsedSideMenu.clear();

    if (this.isCollapsed) {
      this.collapsedComponentRef =
        this.containerCollapsedSideMenu.createComponent(
          CollapsedPanelMenuComponent,
        );

      this.collapsedComponentRef.instance.routes = this.routes();
    } else {
      this.componentRef =
        this.containerSideMenu.createComponent(PanelMenuComponent);

      this.componentRef.instance.routes = this.routes();
      this.componentRef.instance.enforceReportSequence =
        this.enforceReportSequence();
      this.componentRef.instance.allowedNextReportTypes =
        this.vesselReportsStore.vm().allowedNextReportTypes;
    }
  }

  setVisibility(
    menuItem: SideMenuItem | undefined | null,
    visibility: boolean,
    mappingItem: VisibilityMapping,
  ) {
    if (!menuItem) return;

    const nextLevelMappingItems = mappingItem.nextLevel;
    if (!nextLevelMappingItems) menuItem.visible = visibility;

    if (!(nextLevelMappingItems && menuItem.items)) return;

    nextLevelMappingItems.map((item: VisibilityMapping) => {
      const nextLevelMenuItems = menuItem.items;
      if (!nextLevelMenuItems) return;

      const itemIndex = this.findMenuItemIndex(nextLevelMenuItems, item.label);
      const menuItemNextLevel = nextLevelMenuItems[itemIndex];
      this.setVisibility(menuItemNextLevel, visibility, item);
    });
  }

  findMenuItemIndex = (
    menuItems: SideMenuItem[] | undefined,
    label: string,
  ): number => {
    if (!menuItems) return -1;
    return menuItems.findIndex((item: SideMenuItem) => {
      return item.label === label;
    });
  };

  //use changeRoutes if a visibility variable shows some items but also hides others
  changeRoutes = (changeVariable: boolean, mapping: VisibilityMapping[]) => {
    mapping.map((item) => {
      const itemIndex = this.findMenuItemIndex(this.routes(), item.label);
      const menuItem = this.routes()[itemIndex];
      this.setVisibility(menuItem, changeVariable, item);
    });
  };

  changeMenu(changeVariable: boolean, mapping: VisibilityMapping[]) {
    this.changeRoutes(changeVariable, mapping);
    this.resetMenu();
  }

  changeIsInternal() {
    this.isInternal = !this.isInternal;
    this.changeMenu(this.isInternal, ShowOnInternalOnly);
  }

  changeIsFromNavFleet() {
    this.hasEnteredAppFromNavFleet = !this.hasEnteredAppFromNavFleet;
    this.changeMenu(this.hasEnteredAppFromNavFleet, ShowFromNavFleet);
  }

  changeIsCrewDashboard = () => {
    this.isCrewDashboardClient = !this.isCrewDashboardClient;
    this.changeMenu(this.isCrewDashboardClient, ShowCrewDashboardClient);
  };

  changeHasIncompleteVesselRegularReport = () => {
    this.hasIncompleteVesselRegularReport =
      !this.hasIncompleteVesselRegularReport;

    this.changeRoutes(
      this.hasIncompleteVesselRegularReport,
      ShowOnIncompleteVesselReportReg,
    );
    this.changeMenu(
      !this.hasIncompleteVesselRegularReport,
      HideOnAnyIncompleteVesselReport,
    );
  };
  changeHasIncompleteVesselOffshoreReport = () => {
    this.hasIncompleteVesselOffshoreReport =
      !this.hasIncompleteVesselOffshoreReport;

    this.changeRoutes(
      this.hasIncompleteVesselOffshoreReport,
      ShowOnIncompleteVesselReportOffshore,
    );
    this.changeMenu(
      !this.hasIncompleteVesselOffshoreReport,
      HideOnAnyIncompleteVesselReport,
    );
  };
  changeEmissionsClient() {
    this.isEmissionComplianceClient = !this.isEmissionComplianceClient;
    this.changeMenu(this.isEmissionComplianceClient, ShowEmissionsClient);
  }
  changeTypeOfEngineReport(type: EngineReportType) {
    this.typeOfEngineReportInProgress = type;
  }
  updateEngineEditMapping(editString: string) {
    const editEngineVisibilityMapping: VisibilityMapping | undefined =
      this.showIncompleteEngineReport[0]?.nextLevel?.[0];
    if (!editEngineVisibilityMapping?.label) return;
    editEngineVisibilityMapping.label = editString;
  }
  updateEngineReportEditMenuItemLabel(editString: string) {
    const engineReportEditMenuItem = this.routes()?.[2].items?.[1];
    if (!engineReportEditMenuItem) return;
    engineReportEditMenuItem.label = editString;
  }
  changeIsDieselElectricPropulsion() {
    this.isDieselElectricPropulsion = !this.isDieselElectricPropulsion;

    this.changeRoutes(
      this.isDieselElectricPropulsion,
      ShowOnDieselElectricEngineReport,
    );
    this.changeRoutes(
      !this.isDieselElectricPropulsion,
      HideOnDieselElectricEngineReport,
    );

    this.editEngineReportLabel = `Edit ${this.typeOfEngineReportInProgress} Report in Progress`;

    this.updateEngineEditMapping(this.editEngineReportLabel);
    this.updateEngineReportEditMenuItemLabel(this.editEngineReportLabel);

    this.resetMenu();
  }
  changeHasEngineReportInProgress() {
    this.hasIncompleteEngineReport = !this.hasIncompleteEngineReport;
    this.changeRoutes(
      this.hasIncompleteEngineReport,
      ShowOnIncompleteEngineReport,
    );
    this.changeMenu(
      !this.hasIncompleteEngineReport,
      HideOnIncompleteEngineReport,
    );
  }

  private updateVesselReportMenuState(
    vm: {
      canCreateReport: boolean;
      allowedNextReportTypes: string[];
      incompleteReportId: string | null;
    },
    localReportId: string | null,
    enforceSequence: boolean,
  ): void {
    const inProgressReportId = localReportId ?? vm.incompleteReportId;
    if (inProgressReportId) {
      this.showInProgressReportMenu(inProgressReportId);
      return;
    }

    this.showCreateReportMenu(vm.allowedNextReportTypes, enforceSequence);
  }

  private showInProgressReportMenu(reportId: string): void {
    this.changeRoutes(false, HideOnAnyIncompleteVesselReport);
    this.setEditReportRoute(reportId);
    this.changeRoutes(true, ShowEditReportInProgress);
  }

  private showCreateReportMenu(
    allowedNextReportTypes: string[],
    enforceSequence: boolean,
  ): void {
    this.changeRoutes(false, ShowEditReportInProgress);
    this.changeRoutes(true, HideOnAnyIncompleteVesselReport);

    if (enforceSequence) {
      const allowedLabels = new Set<string>();
      for (const type of allowedNextReportTypes) {
        const label = REPORT_TYPE_MENU_LABELS[type];
        if (label) allowedLabels.add(label);
      }

      for (const label of ALL_CREATE_MENU_LABELS) {
        const mapping: VisibilityMapping[] = [
          {
            label: 'Vessel Reports',
            nextLevel: [
              {
                label: 'Create New Report',
                nextLevel: [{ label }],
              },
            ],
          },
        ];
        this.changeRoutes(allowedLabels.has(label), mapping);
      }
    }
  }

  private setEditReportRoute(reportId: string): void {
    const vesselReportsItem = this.routes().find(
      (r) => r.id === 'vessel-reports',
    );
    const editItem = vesselReportsItem?.items?.find(
      (item) => item.label === 'Edit Report in Progress',
    );
    if (editItem) {
      editItem['route'] = `vessel-reports/${reportId}`;
    }
  }

  private updateEngineReportMenuState(
    incompleteReportId: string | null,
    localReportId: string | null,
  ): void {
    const inProgressReportId = localReportId ?? incompleteReportId;
    if (inProgressReportId) {
      this.showInProgressEngineReportMenu(inProgressReportId);
      return;
    }

    this.showCreateEngineReportMenu();
  }

  private showInProgressEngineReportMenu(reportId: string): void {
    this.changeRoutes(false, HideOnIncompleteEngineReport);
    this.setEditEngineReportRoute(reportId);
    this.changeRoutes(true, ShowOnIncompleteEngineReport);

    this.resetMenu();
  }

  private showCreateEngineReportMenu(): void {
    this.changeRoutes(false, ShowOnIncompleteEngineReport);
    this.changeRoutes(true, HideOnIncompleteEngineReport);

    this.resetMenu();
  }

  private setEditEngineReportRoute(reportId: string): void {
    const engineReportItem = this.routes().find(
      (r) => r.id === 'engine-reports',
    );
    const editItem = engineReportItem?.items?.find(
      (item) => item.label === this.editEngineReportLabel,
    );
    if (editItem) {
      editItem['route'] = `engine-reports/${reportId}`;
    }
  }

  private updateOffshoreReportMenuState(
    incompleteReportId: string | null,
  ): void {
    if (incompleteReportId) {
      this.showInProgressOffshoreReportMenu(incompleteReportId);
      return;
    }

    this.showCreateOffshoreReportMenu();
  }

  private showInProgressOffshoreReportMenu(reportId: string): void {
    this.changeRoutes(false, HideOnAnyIncompleteOffshoreReport);
    this.setEditOffshoreReportRoute(reportId);
    this.changeRoutes(true, ShowEditOffshoreReportInProgress);
  }

  private showCreateOffshoreReportMenu(): void {
    this.changeRoutes(false, ShowEditOffshoreReportInProgress);
    this.changeRoutes(true, HideOnAnyIncompleteOffshoreReport);
  }

  private setEditOffshoreReportRoute(reportId: string): void {
    const offshoreReportsItem = this.routes().find(
      (r) => r.id === 'offshore-reports',
    );
    const editItem = offshoreReportsItem?.items?.find(
      (item) => item.label === 'Edit Report in Progress',
    );
    if (editItem) {
      editItem['route'] = `offshore-reports/${reportId}`;
    }
  }
}
